import {
    addError,
    ValidityState,
    replaceError,
    getInitializedValidityState
} from "js/types";
import { toNumber } from "js/helpers/utility";
import moment from "moment";

export enum Validations {
    REQUIRED = "required",
    REQUIRED_NUMERIC = "required_number",
    EMAIL = "email",
    NON_ZERO = "non_zero",
    MIN_VALUE = "min_value",
    MAX_VALUE = "max_value",
    MIN_DATE = "min_date",
    MAX_DATE = "max_date",
    VALID_DATE = "valid_date"
}

export interface InputValidationRule {
    rule: Validations;
    message?: string;
    limit?: number;
    data?: any;
}

export type ErrorsObj<T extends string> = Partial<Record<T, boolean>>;
export type ValidationRules<T extends string> = Partial<
    Record<T, InputValidationRule[]>
>;

const validators = {
    [Validations.EMAIL]: {
        validate: (str: string) => {
            const mailFormat = new RegExp(
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
            if (str && mailFormat.exec(str)) {
                return true;
            } else {
                return false;
            }
        },
        formatMessage: () => `Entered email is invalid`
    },
    [Validations.REQUIRED]: {
        validate: value =>
            !!(
                value !== undefined &&
                value !== null &&
                value.toString().trim().length > 0
            ),
        formatMessage: (field: string) => `${field} is required`
    },
    [Validations.NON_ZERO]: {
        validate: (value: any) => toNumber(value) !== 0,
        formatMessage: (field: string) => `${field} can't be zero`
    },
    [Validations.MIN_VALUE]: {
        validate: (value: any, limit = 0) => toNumber(value) >= limit,
        formatMessage: (field: string, limit: number) =>
            `${field} can't be less than ${limit}`
    },
    [Validations.MAX_VALUE]: {
        validate: (value: any, limit = Infinity) => {
            return toNumber(value) <= limit;
        },
        formatMessage: (field: string, limit: number) =>
            `${field} can't be greatar than ${limit}`
    },
    [Validations.VALID_DATE]: {
        validate: (value: any) => moment(value, "YYYY-MM-DD", true).isValid(),
        formatMessage: () => `Entered birth date is invalid`
    },
    [Validations.MIN_DATE]: {
        validate: (value: any, data: any) => {
            const currentDate = moment(value, "YYYY-MM-DD", true);
            const minDate = moment(data, "YYYY-MM-DD", true);
            if (currentDate.isBefore(minDate)) {
                return false;
            } else {
                return true;
            }
        },
        formatMessage: (field: string, limit: number) => `Date is out of scope`
    },
    [Validations.MAX_DATE]: {
        validate: (value: any, data: any) => {
            const currentDate = moment(value, "YYYY-MM-DD", true);
            const maxDate = moment(data, "YYYY-MM-DD", true);
            if (currentDate.isAfter(maxDate)) {
                return false;
            } else {
                return true;
            }
        },
        formatMessage: (field: string, limit: number) => `Date is out of scope`
    }
};

export function applyValidations(
    state: ValidityState,
    rules: InputValidationRule[],
    value: any,
    field: string
): ValidityState {
    let isValid = true;
    rules.forEach(rule => {
        let result = false;
        if (rule.data != null) {
            result = !validators[rule.rule].validate(value, rule.data);
        } else {
            result = !validators[rule.rule].validate(value);
        }

        if (result) {
            isValid = false;
            state = replaceError(state, field, {
                code: rule.rule,
                message: rule.message
                    ? rule.message
                    : validators[rule.rule].formatMessage(field)
            });
        }
    });
    if (isValid) {
        state = replaceError(state, field, null);
    }
    return state;
}

export function getInitializedValidityStateFromRules(
    rules: ValidationRules<string>,
    values: { [index: string]: any },
    state = getInitializedValidityState([], [])
) {
    for (const k of Object.keys(rules)) {
        state = applyValidations(state, rules[k], values[k], k);
    }
    return state;
}
